// Car-related types
export enum TransmissionType {
  MANUAL = "Manual",
  AUTOMATIC = "Automatic",
  CVT = "CVT"
}

export enum FuelType {
  PETROL = "Petrol",
  DIESEL = "Diesel",
  HYBRID = "Hybrid",
  ELECTRIC = "Electric"
}

export enum CarCondition {
  NEW = "New",
  USED = "Used",
  CERTIFIED_PRE_OWNED = "Certified Pre-Owned"
}

export interface Car {
  id: string;
  make: string;
  model: string;
  year: number;
  price: number;
  mileage: number;
  color: string;
  transmission: string;
  fuel_type: string;
  condition: string;
  cubic_capacity: string;
  horsepower: number;
  images: string[];
  description: string;
  features: string[];
  is_available: boolean;
  created_at: string;
  updated_at: string;
}

export interface CarCreate {
  id: string;
  numplate: string;
  make: string;
  model: string;
  year: number;
  price: number;
  price_bought: number;
  mileage: number;
  color: string;
  transmission: TransmissionType;
  fuel_type: FuelType;
  condition: CarCondition;
  cubic_capacity: string;
  horsepower: number;
  images: string[];
  description: string;
  features: string[];
  is_available?: boolean;
  status?: VehicleStatus;
  price_sold?: number | null;
  sold_at?: string | null;
  total_expenses?: number;
  total_cost?: number;
  profit?: number | null;
  projected_profit?: number;
}

export interface UpdateCar {
  imagesToRemoveUrls: String[];
  updatedVehicleFields: Partial<CarCreate>;
}

// Expense-related types
export enum ExpenseCategory {
  REPAIR = "REPAIR",
  SERVICE = "SERVICE",
  PARTS = "PARTS",
  DETAILING = "DETAILING",
  TRANSPORT = "TRANSPORT",
  REGISTRATION = "REGISTRATION",
  INSURANCE = "INSURANCE",
  OTHER = "OTHER",
}

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  [ExpenseCategory.REPAIR]: "Repair",
  [ExpenseCategory.SERVICE]: "Service",
  [ExpenseCategory.PARTS]: "Parts",
  [ExpenseCategory.DETAILING]: "Detailing",
  [ExpenseCategory.TRANSPORT]: "Transport",
  [ExpenseCategory.REGISTRATION]: "Registration / Road Tax",
  [ExpenseCategory.INSURANCE]: "Insurance",
  [ExpenseCategory.OTHER]: "Other",
};

export interface VehicleExpense {
  id: string;
  vehicle_id: string;
  category: ExpenseCategory;
  description?: string | null;
  amount: number;
  expense_date: string;
  created_by?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleProfitSummary {
  vehicle_id: string;
  status?: VehicleStatus;
  sold_at?: string | null;
  price_bought: number;
  price_sold: number | null;
  listing_price: number;
  total_expenses: number;
  total_cost: number;
  profit: number | null;
  projected_profit: number;
}

export interface VehicleExpensesResponse {
  expenses: VehicleExpense[];
  summary: VehicleProfitSummary;
}

export interface VehicleExpenseInput {
  category: ExpenseCategory;
  description?: string;
  amount: number;
  expense_date: string;
}

// Overhead-related types
export enum OverheadCategory {
  RENT = "RENT",
  SALARIES = "SALARIES",
  UTILITIES = "UTILITIES",
  MARKETING = "MARKETING",
  INSURANCE = "INSURANCE",
  OTHER = "OTHER",
}

export const OVERHEAD_CATEGORY_LABELS: Record<OverheadCategory, string> = {
  [OverheadCategory.RENT]: "Rent",
  [OverheadCategory.SALARIES]: "Salaries",
  [OverheadCategory.UTILITIES]: "Utilities",
  [OverheadCategory.MARKETING]: "Marketing",
  [OverheadCategory.INSURANCE]: "Insurance",
  [OverheadCategory.OTHER]: "Other",
};

export interface Overhead {
  id: string;
  category: OverheadCategory;
  description?: string | null;
  amount: number;
  expense_date: string;
  created_by?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OverheadInput {
  category: OverheadCategory;
  description?: string;
  amount: number;
  expense_date: string;
}

export interface OverheadsResponse {
  overheads: Overhead[];
  total: number;
  totalAmount: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Report-related types
export interface SalesReportMonth {
  month: string;
  vehiclesSold: number;
  revenue: number;
  costOfVehicles: number;
  soldVehicleExpenses: number;
  grossProfit: number;
  monthlyExpenses: number;
  overhead: number;
  netProfit: number;
}

export type SalesReportTotals = Omit<SalesReportMonth, "month">;

export interface SalesReportVehicle {
  id: string;
  numplate: string;
  make: string;
  model: string;
  year: number;
  sold_at: string;
  price_bought: number;
  total_expenses: number;
  total_cost: number;
  price_sold: number | null;
  profit: number | null;
}

export interface CategoryTotal {
  category: string;
  total: number;
}

export interface SalesReport {
  from: string;
  to: string;
  months: SalesReportMonth[];
  totals: SalesReportTotals;
  soldVehicles: SalesReportVehicle[];
  soldWithoutPrice: number;
  expensesByCategory: CategoryTotal[];
  overheadByCategory: CategoryTotal[];
}

export enum VehicleStatus {
    AVAILABLE = 'AVAILABLE',
    SOLD = 'SOLD'
}

export type VehicleResponse = {
    vehicles: Car[];
    total: number;
  };

// Inquiry-related types
export interface Inquiry {
  id: string;
  car_id: string;
  full_name: string;
  email: string;
  phone_number: string;
  message: string;
  createdAt: string;
}

export interface InquiryCreate {
  car_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  message: string;
}

// Filter types
export interface CarFilters {
  search?: string;
  make: string;
  minPrice: number | undefined;
  maxPrice: number | undefined;
  minYear: number | undefined;
  maxYear: number | undefined;
  fuelType?: FuelType | "all";
  transmission?: TransmissionType | "all";
  condition?: CarCondition | "all";
}

// Form types
export interface InquiryFormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  message: string;
}

export interface CarFormData {
  numplate: string;
  make: string;
  model: string;
  year: number;
  price: number;
  price_bought: number;
  price_sold?: number;
  mileage: number;
  color: string;
  transmission: TransmissionType;
  fuel_type: FuelType;
  condition: CarCondition;
  cubic_capacity: string;
  horsepower: number;
  images: string[];
  description: string;
  features: string[];
}

// API response types
export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface ApiError {
  message: string;
  status: number;
}

// Component prop types
export interface CarCardProps {
  car: Car;
  onViewDetails: (car: Car) => void;
  onInquire: (car: Car) => void;
}

export interface CarFiltersProps {
  filters: CarFilters;
  onFilterChange: (key: keyof CarFilters, value: string | undefined) => void;
  onClearFilters: () => void;
}

export interface CarDetailsModalProps {
  car: Car | null;
  isOpen: boolean;
  onClose: () => void;
  onInquire: (car: Car) => void;
}

export interface InquiryModalProps {
  car: Car | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (carId: string, inquiryData: InquiryFormData) => Promise<void>;
}